import { InjectionToken } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { AppBreadcrumb } from '../../layout/breadcrumb/app-breadcrumb.component';

export type BereichId = 'benutzer' | 'projekte' | 'ki' | 'rechner' | 'auswahllisten' | 'erscheinungsbild';

export type VerwaltungBadge = 'eingeladen' | 'rechnerOffline';

export interface VerwaltungBereichDef {
  id: BereichId;
  titel: string;
  untertitel: string | null;
  icon: string;
  badge: VerwaltungBadge | null;
  /** Rendert der Bereich `app-vw-kopf` selbst, zeigt die Seite keinen Kopf für ihn. */
  eigenerKopf: boolean;
}

export interface VerwaltungGruppe {
  label: string;
  bereiche: VerwaltungBereichDef[];
}

function bereich(
  id: BereichId,
  icon: string,
  extras: Partial<Pick<VerwaltungBereichDef, 'untertitel' | 'badge' | 'eigenerKopf'>> = {},
): VerwaltungBereichDef {
  return {
    id,
    titel: `verwaltung.bereich.${id}.titel`,
    untertitel: extras.untertitel ?? null,
    icon,
    badge: extras.badge ?? null,
    eigenerKopf: extras.eigenerKopf ?? false,
  };
}

export const VERWALTUNG_GRUPPEN: VerwaltungGruppe[] = [
  {
    label: 'verwaltung.gruppe.personen',
    bereiche: [
      bereich('benutzer', 'group', {
        untertitel: 'verwaltung.bereich.benutzer.untertitel',
        badge: 'eingeladen',
        eigenerKopf: true,
      }),
      bereich('projekte', 'folder', { eigenerKopf: true }),
    ],
  },
  {
    label: 'verwaltung.gruppe.anbindungen',
    bereiche: [
      bereich('ki', 'extension', { untertitel: 'verwaltung.bereich.ki.untertitel' }),
      bereich('rechner', 'settings', { badge: 'rechnerOffline', eigenerKopf: true }),
    ],
  },
  {
    label: 'verwaltung.gruppe.vorgaben',
    bereiche: [
      bereich('auswahllisten', 'table_chart', { untertitel: 'verwaltung.bereich.auswahllisten.untertitel' }),
      bereich('erscheinungsbild', 'palette', { untertitel: 'verwaltung.bereich.erscheinungsbild.untertitel' }),
    ],
  },
];

export const VERWALTUNG_BEREICHE = new InjectionToken<VerwaltungGruppe[]>('VERWALTUNG_BEREICHE', {
  factory: () => VERWALTUNG_GRUPPEN,
});

export const STANDARD_BEREICH: BereichId = 'benutzer';

const BEREICH_IDS = VERWALTUNG_GRUPPEN.flatMap((g) => g.bereiche.map((b) => b.id));

/** Gültige Bereichs-Id aus dem Query-Parameter, sonst `null`. */
export function bereichAusParam(wert: string | null): BereichId | null {
  return BEREICH_IDS.find((id) => id === wert) ?? null;
}

export function verwaltungBreadcrumbSuffix(route: ActivatedRouteSnapshot): AppBreadcrumb[] {
  const id = bereichAusParam(route.queryParamMap.get('bereich')) ?? STANDARD_BEREICH;
  return [{ label: `verwaltung.bereich.${id}.titel`, url: '/settings' }];
}
